import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useCallback } from 'react';
import { getHttpClient } from '@vnetwork/margarita-core/http';
import type { Envelope } from './envelope';

const serviceApiV3 = getHttpClient('serviceApiV3');

// ============================================
// Types  (subset — full Services feature lands in Phase 5)
// ============================================

export interface ServiceProvider {
  id: string;
  name: string;
  display_name: string;
  /** Provider category, e.g. "cdn", "cloud" — used to group services. */
  type: string;
  legacy_scope?: string;
}

export interface AvailableService {
  id: string;
  service_name: string;
  organization_id: string;
  provider_id: string;
  provider?: ServiceProvider;
  status: string;
  zones_count: number;
  /** The Compute metadata record this service is scoped to (compute services only). */
  metadata_id?: string;
  created_at: string;
  updated_at?: string;
}

/**
 * Human label for a service. Uses `service_name` because it's unique per
 * service (e.g. "Eagle CDN - 8064 (THVL)") — the provider display name repeats
 * across services and is shown as the group header instead.
 */
export function serviceLabel(s: AvailableService): string {
  return s.service_name || s.provider?.display_name || s.provider?.name || s.id;
}

/** Grouping bucket for a service (provider category, or "other"). */
export function serviceScope(s: AvailableService): string {
  return s.provider?.type?.trim() || 'other';
}

export const serviceKeys = {
  all: ['v3', 'services'] as const,
  list: (search: string, perPage: number, topLevel: boolean) =>
    ['v3', 'services', { search, perPage, topLevel }] as const,
  mine: ['v3', 'services', 'mine'] as const,
  recent: (sort: string, limit: number) =>
    ['v3', 'services', 'recent', { sort, limit }] as const,
  zones: (serviceId: string) => ['v3', 'services', serviceId, 'zones'] as const,
};

export interface ServiceZone {
  id: string;
  service_id: string;
  zone_id: string;
  zone?: {
    id: string;
    zone_name: string;
    zone_description?: string;
    zone_type?: string;
  };
  uid: string;
  status: string;
  quota_limit?: number;
  created_at?: string;
  updated_at?: string;
}

/**
 * Zones (WAAP/CDN entities) for a specific service. The `uid` field is the
 * Entity ID used in monitor configs (customer_id). Enabled only when a
 * serviceId is provided.
 */
export function useServiceZones(serviceId: string | undefined) {
  return useQuery<ServiceZone[]>({
    queryKey: serviceId ? serviceKeys.zones(serviceId) : ['v3', 'services', 'zones-disabled'],
    enabled: !!serviceId,
    staleTime: 60_000,
    queryFn: async () => {
      const res = await serviceApiV3.get<
        Envelope<{ service_zones?: ServiceZone[]; zones?: ServiceZone[] }>
      >(`/services/${serviceId}/zones`);
      return res.data?.data?.service_zones ?? res.data?.data?.zones ?? [];
    },
  });
}

/** Legacy provider scope of a service (e.g. "Internal.FastChannel"). */
export function serviceProviderScope(s: AvailableService): string {
  return s.provider?.legacy_scope?.trim() || 'unknown';
}

export interface ServicesPage {
  services: AvailableService[];
  total: number;
  page: number;
  perPage: number;
}

interface ServicesEnvelope {
  services: AvailableService[];
  total: number;
  page: number;
  per_page: number;
}

/**
 * Search the service catalog (used by the team permission picker). The catalog
 * is paginated (~300+ services), so we always send a `search` term + `per_page`
 * and let the backend narrow results. `total` reflects all matches, not the page.
 *
 * `topLevel` hides CDN child (per-provider) services so only orchestrator
 * umbrellas are pickable — a share then grants the family, never a single
 * per-provider canonical.
 */
export function useServices(params?: { search?: string; perPage?: number; topLevel?: boolean }) {
  const search = (params?.search ?? '').trim();
  const perPage = params?.perPage ?? 50;
  const topLevel = params?.topLevel ?? false;
  return useQuery<ServicesPage>({
    queryKey: serviceKeys.list(search, perPage, topLevel),
    // Keep showing the previous results while a new search is in flight so the
    // list doesn't flash empty on every keystroke.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await serviceApiV3.get<Envelope<ServicesEnvelope>>('/services', {
        params: { search: search || undefined, per_page: perPage, page: 1, top_level: topLevel || undefined },
      });
      const d = res.data?.data;
      return {
        services: d?.services ?? [],
        total: d?.total ?? 0,
        page: d?.page ?? 1,
        perPage: d?.per_page ?? perPage,
      };
    },
  });
}

/**
 * All of the customer's provisioned services (owned + shared). The backend caps
 * the page size (~50), so we page through to collect them all — mirrors the
 * legacy `useMyServices`.
 */
export function useMyServices(options?: { enabled?: boolean }) {
  return useQuery<AvailableService[]>({
    queryKey: serviceKeys.mine,
    enabled: options?.enabled ?? true,
    staleTime: 30_000,
    queryFn: async () => {
      const PER_PAGE = 50;
      const MAX_PAGES = 40;
      const all: AvailableService[] = [];
      let page = 1;
      let total = Infinity;
      while (all.length < total && page <= MAX_PAGES) {
        const res = await serviceApiV3.get<Envelope<ServicesEnvelope>>('/services', {
          params: { per_page: PER_PAGE, page },
        });
        const d = res.data?.data;
        const batch = d?.services ?? [];
        all.push(...batch);
        total = d?.total ?? all.length;
        if (batch.length < PER_PAGE) break;
        page += 1;
      }
      return all;
    },
  });
}

/** A service the user accessed, with backend-tracked access metadata. */
export interface RecentService extends AvailableService {
  access_count: number;
  last_accessed_at: string;
}

interface RecentEnvelope {
  services: RecentService[];
  total: number;
}

/**
 * The user's most recently (or most frequently) accessed services — tracked
 * server-side each time a service detail is opened. `sort: 'frequent'` ranks by
 * access count instead of recency. Returns [] until the user has opened a
 * service at least once; the caller falls back to a created_at sort.
 */
export function useRecentServices(params?: { sort?: 'recent' | 'frequent'; limit?: number }) {
  const sort = params?.sort ?? 'recent';
  const limit = params?.limit ?? 5;
  return useQuery<RecentService[]>({
    queryKey: serviceKeys.recent(sort, limit),
    staleTime: 30_000,
    queryFn: async () => {
      const res = await serviceApiV3.get<Envelope<RecentEnvelope>>('/services/recent', {
        params: { sort, limit },
      });
      return res.data?.data?.services ?? [];
    },
  });
}

/**
 * Returns a fire-and-forget callback that records a service access. Call it when
 * a user opens a service (the per-product consoles use their own data endpoints,
 * so access can't be inferred server-side from reads). On success it invalidates
 * the recent-services cache so the dashboard reflects the new access on return.
 */
export function useRecordServiceAccess() {
  const qc = useQueryClient();
  return useCallback(
    (serviceId: string | undefined) => {
      if (!serviceId) return;
      serviceApiV3
        .post(`/services/${serviceId}/access`)
        .then(() => {
          qc.invalidateQueries({ queryKey: ['v3', 'services', 'recent'] });
        })
        .catch(() => {
          // Best-effort: tracking must never disrupt navigation.
        });
    },
    [qc],
  );
}
